import * as React from "react";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { ReactComponent as HomeIcon } from "../../assets/home.svg";
import { ReactComponent as ModIcon } from "../../assets/mod.svg";
import { ReactComponent as TrainingIcon } from "../../assets/training.svg";
import { ReactComponent as PaymentIcon } from "../../assets/dollar.svg";
import { ReactComponent as UserIcon } from "../../assets/user.svg";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import EditIcon from "@mui/icons-material/Edit";
import { Avatar, Badge, Input, Menu, MenuItem, Tooltip } from "@mui/material";
import Users from "../Tabs/Users";
import Mod from "../Tabs/MOD";
import Training from "../Tabs/Training";
import { useDispatch, useSelector } from "react-redux";
import {
  editUser,
  getCurrentUser,
  logoutUser,
  setPendingAgreementChecked
} from "../../features/auth/authSlice";
import getInitials from "../../utils/getInitials";
import {
  AdminPanelSettings,
  ArrowBack,
  Description,
  Discount,
  Email,
  EmojiEvents,
  EmojiFoodBeverage,
  FormatListNumberedRtl,
  Handshake,
  Link,
  LiveHelp,
  WorkspacePremium,
  Forum
} from "@mui/icons-material";
import useSignedUrl from "../../utils/useSignedUrl";
import { updateCode } from "../../api/userApi";
import ConnectProfileModal from "../Modals/ConnectLinkModal";
import EditAdmin from "../Modals/EditAdmin";
import Notes from "../Extra/Notes";
import AdminPortal from "../Tabs/AdminPortal";
import SuperAdminHome from "../Tabs/SuperAdminHome";
import AdminHome from "../Tabs/AdminHome";
import CouponManagement from "../Extra/Coupons";
import Challenges from "../Extra/Challenges";
import LevelsForm from "../Extra/Levels";
import CustomFoodForm from "../Extra/CustomFood";
import SuggestionModal from "../Modals/SuggestionLevelModal";
import CodeBlocks from "../Extra/Codes";
import AutomatedEmails from "../Extra/AutomatedEmails";
import AgreementModal from "../Modals/CoachAgreement";
import ManualCopyPaste from "../Extra/ManualCopyPaste";
import WeeklyQA from "../Extra/WeeklyQA";
import Masterclass from "../Tabs/Masterclass";
import AdminMessagingHub from "../Messaging/AdminMessagingHub";
import { io as socketIO } from "socket.io-client";
import useFaviconBadge from "../../utils/useFaviconBadge";
import messagingBroadcast from "../../utils/messagingBroadcast";
import {
  appendMessage as appendMsgAction,
  fetchAdminConversations,
  setActiveConversation,
  clearUnreadForConversation,
} from "../../features/messaging/messagingSlice";

const MessagingBadgeIcon = () => {
  const totalUnread = useSelector((state) => state.messaging?.totalUnread || 0);
  return (
    <Badge badgeContent={totalUnread} color="error" max={99}>
      <Forum sx={{ color: "#9ca3af" }} />
    </Badge>
  );
};

const drawerWidth = 265;
const StyledPaymentIcon = styled(PaymentIcon)({
  width: 22,
  height: 17,
  "& path": {
    fill: "#9CA3AF" // Ensure you target the path element correctly
  }
});
const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen
  }),
  overflowX: "hidden"
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 10px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 24px)`
  }
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  height: "90px",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(4, 1),
  ...theme.mixins.toolbar
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open"
})(({ theme, open }) => ({
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  }),
  ...(!open && {
    marginLeft: `calc(${theme.spacing(7)} + 10px)`,
    width: `calc(100% - calc(${theme.spacing(7)} + 33px))`
  })
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open"
})(({ theme, open }) => ({
  width: drawerWidth - 20,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  overflow: "visible",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": {
      ...openedMixin(theme),
      overflow: "visible"
    }
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": {
      ...closedMixin(theme),
      overflow: "visible"
    }
  })
}));

const AdminDashboard = ({ isFailedPayment }) => {
  const currentAdmin = useSelector((state) => state.adminauth.admin);
  const [open, setOpen] = React.useState(true);
  const [activeTab, setActiveTab] = React.useState("Users");
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [isAddingMeal, setIsAddingMeal] = React.useState(false);
  const [isAddingTraining, setIsAddingTraining] = React.useState(false);
  const [isAddingMasterclass, setIsAddingMasterclass] = React.useState(false);
  const [isEditing, setIsEditing] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  const [code, setCode] = React.useState("");
  const [codeStates, setCodeStates] = React.useState({
    affiliate: {
      code: "",
      isEditing: false,
      isCopied: false
    },
    coaches: {
      code: "",
      isEditing: false,
      isCopied: false
    }
  });
  const [openModal, setOpenModal] = React.useState(false);
  const [addLevelModal, setAddLevelModal] = React.useState(false);
  const [agreementModal, setAgreementModal] = React.useState(false);
  const [useTemplateData, setUseTemplateData] = React.useState(false);
  const dispatch = useDispatch();
  const adminSocketRef = React.useRef(null);

  // Persistent socket connection for real-time messaging across all tabs
  React.useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) return;
    const socketUrl = new URL(process.env.REACT_APP_BACKEND_URL).origin;
    const socket = socketIO(socketUrl, {
      auth: { token },
      transports: ["websocket"]
    });
    socket.on("new_message", (msg) => {
      dispatch(appendMsgAction(msg));
    });
    adminSocketRef.current = socket;
    dispatch(fetchAdminConversations());
    return () => socket.disconnect();
  }, [dispatch]);

  // Sync "conversation read" events from other open tabs
  React.useEffect(() => {
    const handler = (event) => {
      if (event.data?.type === "conv_read") {
        dispatch(clearUnreadForConversation(event.data.conversationId));
      }
    };
    messagingBroadcast.addEventListener("message", handler);
    return () => messagingBroadcast.removeEventListener("message", handler);
  }, [dispatch]);

  // Refetch conversations whenever a message arrives for an unknown conversation
  const needsRefetch = useSelector((state) => state.messaging?.needsRefetch);
  React.useEffect(() => {
    if (needsRefetch) {
      dispatch(fetchAdminConversations());
    }
  }, [needsRefetch, dispatch]);

  // Update favicon + tab title with unread count
  const totalUnreadDM = useSelector(
    (state) => state.messaging?.totalUnread || 0
  );
  useFaviconBadge(totalUnreadDM);

  // Deep-link from email: ?openConversation=<id>
  const conversationsForLink = useSelector(
    (state) => state.messaging?.conversations || []
  );
  const [pendingConvLink, setPendingConvLink] = React.useState(null);
  React.useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const cid = params.get("openConversation");
      if (cid) {
        setActiveTab("Messaging");
        setPendingConvLink(cid);
        params.delete("openConversation");
        const qs = params.toString();
        window.history.replaceState(
          {},
          "",
          window.location.pathname + (qs ? `?${qs}` : "")
        );
      }
    } catch {}
  }, []);
  React.useEffect(() => {
    if (
      pendingConvLink &&
      conversationsForLink.some((c) => c._id === pendingConvLink)
    ) {
      dispatch({
        type: "messaging/setActiveConversation",
        payload: pendingConvLink
      });
      setPendingConvLink(null);
    }
  }, [pendingConvLink, conversationsForLink, dispatch]);

  // Listen for "Open Messaging" requests from DMToggle / other components
  React.useEffect(() => {
    const handler = async (e) => {
      const conversationId = e?.detail?.conversationId;
      setActiveTab("Messaging");
      if (conversationId) {
        await dispatch(fetchAdminConversations());
        dispatch(setActiveConversation(conversationId));
      }
    };
    window.addEventListener("admin:openMessaging", handler);
    return () => window.removeEventListener("admin:openMessaging", handler);
  }, [dispatch]);

  // Listen for "View user profile" requests from ChatPanel — switch to Users tab
  // and pre-fill the search bar with the user's email so the row surfaces.
  React.useEffect(() => {
    const handler = (e) => {
      const email = e?.detail?.email;
      if (!email) return;
      // Persist in sessionStorage so the Users tab can pick it up after mount
      sessionStorage.setItem("adminUserSearchPrefill", email);
      setActiveTab("Users");
      // Notify the Users tab in case it's already mounted
      window.dispatchEvent(
        new CustomEvent("admin:applyUserSearch", { detail: { email } })
      );
    };
    window.addEventListener("admin:viewUserProfile", handler);
    return () => window.removeEventListener("admin:viewUserProfile", handler);
  }, []);

  React.useEffect(() => {
    setCode(currentAdmin?.referenceData?.code || "");
    setCodeStates((prev) => ({
      affiliate: {
        ...prev.affiliate,
        code: currentAdmin?.referenceData?.affiliateCode || ""
      },
      coaches: {
        ...prev.coaches,
        code: currentAdmin?.referenceData?.coachesCode || ""
      }
    }));
    if (
      currentAdmin?.adminUserId &&
      currentAdmin?.levelDetails &&
      (!Object.keys(currentAdmin?.levelDetails?.levels).length ||
        !Object.keys(currentAdmin?.levelDetails?.diaryLevels).length)
    ) {
      setAddLevelModal(true);
    }
  }, [currentAdmin]);
  React.useEffect(() => {
    if (currentAdmin?.referenceData) {
      if (currentAdmin.referenceData.isPendingCheckAgreement === true) {
        setAgreementModal(true);
      }
    }
  }, [currentAdmin?.referenceData]);
  React.useEffect(() => {
    if (isFailedPayment) setActiveTab("Billing");
  }, [isFailedPayment]);
  const handleNavigateToForm = () => {
    setActiveTab("Levels");
    setUseTemplateData(true);
    setAddLevelModal(false);
  };
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy code: ", err);
    }
  };

  const handleBlur = async () => {
    try {
      if (isEditing) {
        // Call API to update the value
        const response = await updateCode(code, currentAdmin?.referenceId); // Replace with your API call
        if (response.message) {
          setIsEditing(!isEditing); // Toggle edit mode
        }
      } else {
        setIsEditing(!isEditing); // Toggle edit mode
      }
    } catch (error) {
      console.error("Failed to update code:", error);
    }
  };

  const handleCodeChange = (e) => {
    setCode(e.target.value); // Update code when editing
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    await dispatch(logoutUser());
  };

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleTabChange = (tab) => {
    if (tab !== "Messaging") {
      dispatch(setActiveConversation(null));
    }
    setActiveTab(tab);
  };

  const renderContent = () => {
    switch (activeTab) {
      case "Home":
        return <SuperAdminHome />;
      case "MOD":
        return (
          <Mod isAddingMeal={isAddingMeal} setIsAddingMeal={setIsAddingMeal} />
        );
      case "Messaging":
        return <AdminMessagingHub socket={adminSocketRef.current} />;
      case "Users":
        return <Users />;
      case "Programs":
        return (
          <Training
            isAddingTraining={isAddingTraining}
            setIsAddingTraining={setIsAddingTraining}
          />
        );
      case "Masterclass":
        return (
          <Masterclass
            isAddingMasterclass={isAddingMasterclass}
            setIsAddingMasterclass={setIsAddingMasterclass}
          />
        );
      case "Admin Notes":
        return <Notes />;
      case "Coupons":
        return <CouponManagement />;
      case "Challenges":
        return <Challenges />;
      case "Levels":
        return <LevelsForm useTemplateData={useTemplateData} />;
      case "Quick Adds":
        return <CustomFoodForm />;
      case "Automated Emails":
        return <AutomatedEmails isAdmin={true} />;
      case "Coach License Agreement":
        return <ManualCopyPaste currentAdmin={currentAdmin} />;
      case "Nutrition Q & A":
        return <WeeklyQA currentAdmin={currentAdmin} />;
      default:
        return <Typography>Default content</Typography>;
    }
  };

  const adminRenderContent = () => {
    if (currentAdmin?.userType === "Affiliate") {
      switch (activeTab) {
        case "Users":
          return <Users isAdmin={false} />;
        case "Billing":
          return <AdminHome />;
        case "Admin Notes":
          return (
            <AdminPortal
              videoLink={currentAdmin?.adminVideoLink}
              userId={currentAdmin?.userId}
              value={currentAdmin?.adminNotes}
              checkNotesReminder={currentAdmin?.checkNotesReminder}
            />
          );
        default:
          return <Typography>Default content</Typography>;
      }
    } else {
      switch (activeTab) {
        case "Users":
          if (!currentAdmin?.activeSubscription) return <></>;
          return <Users isAdmin={false} />;
        case "Billing":
          return <AdminHome />;
        case "Programs":
          return (
            <Training
              isAddingTraining={isAddingTraining}
              setIsAddingTraining={setIsAddingTraining}
            />
          );
        case "Admin Notes":
          if (!currentAdmin?.activeSubscription) return <></>;
          return (
            <AdminPortal
              videoLink={currentAdmin?.adminVideoLink}
              userId={currentAdmin?.userId}
              value={currentAdmin?.adminNotes}
              checkNotesReminder={currentAdmin?.checkNotesReminder}
            />
          );
        case "Automated Emails":
          return (
            <AutomatedEmails
              isAdmin={false}
              userId={currentAdmin?.referenceId}
            />
          );
        case "Challenges":
          if (!currentAdmin?.activeSubscription) return <></>;
          return <Challenges isFromSubAdmin />;
        case "License Agreement":
          return (
            <AdminPortal
              userId={currentAdmin?.userId}
              value={currentAdmin?.agreementContent}
            />
          );
        case "Messaging":
          if (currentAdmin?.userType !== "Coach")
            return <Typography>Default content</Typography>;
          return <AdminMessagingHub socket={adminSocketRef.current} />;
        default:
          return <Typography>Default content</Typography>;
      }
    }
  };

  const menuItems =
    currentAdmin?.userType === "Affiliate"
      ? [
          { text: "Users", icon: <UserIcon /> },
          {
            text: "Billing",
            icon: <StyledPaymentIcon />
          },
          {
            text: "Admin Notes",
            icon: <Description sx={{ color: "#9ca3af" }} />
          }
        ]
      : !currentAdmin?.adminUserId
        ? [
            { text: "Users", icon: <UserIcon /> },
            ...(currentAdmin?.userType === "Coach"
              ? [
                  {
                    text: "Messaging",
                    icon: <Forum sx={{ color: "#9ca3af" }} />
                  }
                ]
              : []),
            {
              text: "Billing",
              icon: <StyledPaymentIcon />
            },
            ...(currentAdmin?.userType !== "Gym"
              ? [{ text: "Programs", icon: <TrainingIcon /> }]
              : []),
            {
              text: "Admin Notes",
              icon: <AdminPanelSettings sx={{ color: "#9ca3af" }} />
            },
            {
              text: "Challenges",
              icon: <EmojiEvents sx={{ color: "#9ca3af" }} />
            },
            {
              text: "Automated Emails",
              icon: <Email sx={{ color: "#9ca3af" }} />
            },
            ...(currentAdmin?.userType === "Coach"
              ? [
                  {
                    text: "License Agreement",
                    icon: <Handshake sx={{ color: "#9ca3af" }} />
                  }
                ]
              : [])
          ]
        : [
            { text: "Home", icon: <HomeIcon /> },
            { text: "MOD", icon: <ModIcon /> },
            { text: "Users", icon: <UserIcon /> },
            {
              text: "Messaging",
              icon: <Forum sx={{ color: "#9ca3af" }} />
            },
            { text: "Programs", icon: <TrainingIcon /> },
            {
              text: "Masterclass",
              icon: <WorkspacePremium sx={{ color: "#9ca3af" }} />
            },
            {
              text: "Admin Notes",
              icon: <Description sx={{ color: "#9ca3af" }} />
            },
            { text: "Coupons", icon: <Discount sx={{ color: "#9ca3af" }} /> },
            {
              text: "Challenges",
              icon: <EmojiEvents sx={{ color: "#9ca3af" }} />
            },
            {
              text: "Levels",
              icon: <FormatListNumberedRtl sx={{ color: "#9ca3af" }} />
            },
            {
              text: "Quick Adds",
              icon: <EmojiFoodBeverage sx={{ color: "#9ca3af" }} />
            },
            {
              text: "Automated Emails",
              icon: <Email sx={{ color: "#9ca3af" }} />
            },
            {
              text: "Coach License Agreement",
              icon: <Handshake sx={{ color: "#9ca3af" }} />
            },
            {
              text: "Nutrition Q & A",
              icon: <LiveHelp sx={{ color: "#9ca3af" }} />
            }
          ];
  const imgSrc = useSignedUrl(
    currentAdmin?.profilePic,
    process.env.REACT_APP_BACKEND_PROFILE_IMAGE_LOCATION
  );
  const counts = useSelector((state) => state?.users?.pendingMealsCount) || 0;
  return (
    <Box display="flex">
      <CssBaseline />
      <AppBar open={open}>
        <Toolbar
          sx={{
            background: "#F9FAFB",
            justifyContent: "space-between",
            height: "95px"
          }}
        >
          <Box display="flex" alignItems="center" gap="10px">
            {isAddingMeal && activeTab === "MOD" && (
              <ArrowBack
                onClick={(e) => {
                  setIsAddingMeal(false);
                }}
                sx={{ color: "#374151", cursor: "pointer" }}
              />
            )}{" "}
            <Typography
              variant="h5"
              fontWeight="bold"
              color="black"
              noWrap
              component="div"
            >
              {activeTab === "MOD" ? "Meal Of The Day" : activeTab}
            </Typography>
          </Box>

          <Box display="flex" alignItems="center">
            {currentAdmin?.adminUserId && <CodeBlocks />}
            {currentAdmin?.adminUserId ? (
              <></>
            ) : (
              <Box
                sx={{
                  mr: 2,
                  border: "1px solid #d1d5db", // Light gray border
                  borderRadius: "12px", // Rounded corners
                  padding: "12px", // Consistent padding
                  backgroundColor: "#f9fafb", // Subtle background
                  fontFamily: "monospace",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)", // Modern shadow
                  transition: "all 0.3s ease-in-out",
                  "&:hover": {
                    borderColor: "#6366f1", // Highlight border on hover
                    backgroundColor: "#eef2ff" // Light background on hover
                  },
                  display: "flex", // Align content in a row
                  alignItems: "center", // Center align vertically
                  gap: "16px" // Space between elements
                }}
              >
                {/* <IconButton
                  sx={{
                    borderRadius: 1.3,
                    height: 24,
                    width: 24,
                    color: "#1976d2"
                  }}
                > */}
                <Link
                  style={{
                    fill: "#1976d2"
                  }}
                />
                {/* </IconButton> */}
                <Box
                  component="pre"
                  sx={{
                    margin: 0,
                    flexGrow: 1, // Allow text to take available space
                    color: "#1976d2", // Neutral dark text
                    padding: "8px 0"
                  }}
                >
                  {currentAdmin?.affiliateLink}
                </Box>
                <Tooltip title={copied ? "Copied!" : "Copy Affiliate Link"}>
                  <IconButton
                    sx={{
                      color: copied ? "#10b981" : "#9ca3af",
                      transition: "color 0.3s",
                      "&:hover": {
                        color: copied ? "#059669" : "#4b5563"
                      }
                    }}
                    onClick={async () => {
                      await navigator.clipboard.writeText(
                        currentAdmin?.affiliateLink
                      );
                    }}
                  >
                    <FileCopyIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            <IconButton>
              <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                MenuListProps={{
                  "aria-labelledby": "basic-button"
                }}
              >
                <MenuItem onClick={() => setOpenModal(true)}>Profile</MenuItem>
                {/* <MenuItem onClick={handleClose}>My account</MenuItem> */}
                <MenuItem onClick={handleLogout}>Logout</MenuItem>
              </Menu>
              {currentAdmin?.profilePic ? (
                <Box
                  onClick={handleClick}
                  component="img"
                  borderRadius="50%"
                  // border="1px solid #1976d2"
                  height={45}
                  width={45}
                  src={imgSrc}
                  alt="avatar"
                />
              ) : (
                <Avatar onClick={handleClick} height={45} width={45}>
                  {getInitials(currentAdmin?.fullName)}
                </Avatar>
              )}
            </IconButton>
            <EditAdmin
              isSuperAdmin={currentAdmin?.adminUserId}
              role={currentAdmin?.userType}
              editData={currentAdmin}
              open={openModal}
              onClose={() => {
                setOpenModal(false);
              }}
            />
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer
        variant="permanent"
        open={open}
        sx={{
          ".MuiDrawer-paper": {
            background: "#14142B",
            color: "#9CA3AF"
          }
        }}
      >
        <DrawerHeader sx={{ minHeight: "90px" }}>
          <Box
            alignItems="center"
            justifyContent="center"
            display="flex"
            width="100%"
            gap="10px"
            margin="-6px"
          >
            <img
              src="/images/threepillars.png"
              alt="logo"
              style={{ height: "40px" }}
            />
            {open ? (
              <Typography fontSize="large" color="#FFF" variant="bold">
                Three Pillars Method
              </Typography>
            ) : (
              <></>
            )}
          </Box>

          <IconButton
            onClick={open ? handleDrawerClose : handleDrawerOpen}
            sx={{
              marginLeft: "20px",
              position: "absolute",
              padding: 0,
              right: -12, // Position based on drawer state
              transform: "translateY(-50%)",
              backgroundColor: "#1F2937",
              "&:hover": {
                backgroundColor: "#333333"
              },
              top: "5.5%",
              color: "#1976d2",
              border: "1px solid #1976d2",
              zIndex: 1300
            }}
          >
            {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        </DrawerHeader>
        <Divider />
        <List
          sx={{
            marginTop: "20px",
            overflow: "auto",
            scrollbarColor: open ? "rgba(255, 255, 255, 0.5)" : "transparent",
            "&::-webkit-scrollbar": {
              width: open ? "2px" : "0px",
              padding: 0
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: open
                ? "rgba(255, 255, 255, 0.5)"
                : "transparent",
              borderRadius: "6px"
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent"
            }
          }}
        >
          {menuItems.map(({ text, icon }) => (
            <ListItem key={text} sx={{ display: "block" }}>
              <ListItemButton
                onClick={() => {
                  setIsAddingMeal(false);
                  setIsAddingTraining(false);
                  handleTabChange(text);
                }}
                sx={{
                  minHeight: 48,
                  justifyContent: open ? "initial" : "center",
                  px: 2.5
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    position: "relative",
                    mr: open ? 3 : "auto",
                    justifyContent: "center",
                    ...(text === activeTab &&
                      text === "Programs" && {
                        svg: {
                          fill: "#1976d2"
                        }
                      }),
                    ...(text === activeTab &&
                      text !== "Programs" && {
                        svg: {
                          fill: "none",
                          path: {
                            stroke: "#1976d2"
                          }
                        }
                      })
                  }}
                >
                  {text === "MOD" && !open && counts ? (
                    <Badge
                      sx={{
                        "& .MuiBadge-badge": {
                          color: "#FFF",
                          backgroundColor: "#F6541C",
                          position: "absolute", // Set badge position as absolute
                          top: open ? 0 : 2, // Adjust based on drawer state
                          left: open ? "60px" : "11px" // Adjust badge positioning for closed state
                        }
                      }}
                      badgeContent={counts}
                    >
                      {icon}
                    </Badge>
                  ) : text === "Messaging" && !open && totalUnreadDM ? (
                    <Badge
                      sx={{
                        "& .MuiBadge-badge": {
                          color: "#FFF",
                          backgroundColor: "#F6541C",
                          position: "absolute",
                          top: open ? 0 : 2,
                          left: open ? "65px" : "11px"
                        }
                      }}
                      badgeContent={totalUnreadDM}
                    >
                      {icon}
                    </Badge>
                  ) : text === "Admin Notes" &&
                    !open &&
                    currentAdmin?.checkNotesReminder ? (
                    <Badge
                      variant="dot"
                      sx={{
                        "& .MuiBadge-badge": {
                          color: "#FFF",
                          backgroundColor: "#F6541C",
                          left: "unset",
                          minWidth: "11px",
                          height: "11px",
                          right: "-7px",
                          borderRadius: "50%"
                        }
                      }}
                    >
                      {icon}
                    </Badge>
                  ) : (
                    icon
                  )}
                </ListItemIcon>
                {text === "MOD" && open && counts ? (
                  <Badge
                    sx={{
                      "& .MuiBadge-badge": {
                        color: "#FFF",
                        backgroundColor: "#F6541C",
                        left: "25px"
                      }
                    }}
                    badgeContent={counts}
                  >
                    <ListItemText
                      primary={text}
                      sx={{
                        opacity: open ? 1 : 0,
                        ...(text === activeTab && {
                          color: "#1976d2"
                        })
                      }}
                    />
                  </Badge>
                ) : text === "Messaging" && open && totalUnreadDM ? (
                  <Badge
                    sx={{
                      "& .MuiBadge-badge": {
                        color: "#FFF",
                        backgroundColor: "#F6541C",
                        left: "65px"
                      }
                    }}
                    badgeContent={totalUnreadDM}
                  >
                    <ListItemText
                      primary={text}
                      sx={{
                        opacity: open ? 1 : 0,
                        ...(text === activeTab && {
                          color: "#1976d2"
                        })
                      }}
                    />
                  </Badge>
                ) : text === "Admin Notes" &&
                  open &&
                  currentAdmin?.checkNotesReminder ? (
                  <Badge
                    variant="dot"
                    sx={{
                      "& .MuiBadge-badge": {
                        color: "#FFF",
                        backgroundColor: "#F6541C",
                        left: "unset",
                        minWidth: "11px",
                        height: "11px",
                        right: "-7px",
                        borderRadius: "50%"
                      }
                    }}
                  >
                    <ListItemText
                      primary={text}
                      sx={{
                        opacity: open ? 1 : 0,
                        ...(text === activeTab && {
                          color: "#1976d2"
                        })
                      }}
                    />
                  </Badge>
                ) : (
                  <ListItemText
                    primary={text}
                    sx={{
                      whiteSpace: "normal !important",
                      opacity: open ? 1 : 0,
                      ...(text === activeTab && {
                        color: "#1976d2"
                      })
                    }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Drawer>
      {addLevelModal ? (
        <SuggestionModal
          open={addLevelModal}
          handleClose={() => setAddLevelModal(false)}
          handleNavigate={handleNavigateToForm}
        />
      ) : (
        <></>
      )}
      {!currentAdmin?.adminUserId && (
        <ConnectProfileModal admin={currentAdmin} />
      )}
      {currentAdmin &&
        currentAdmin?.userType === "Coach" &&
        currentAdmin?.referenceData?.isPendingCheckAgreement && (
          <AgreementModal
            value={currentAdmin?.agreementContent}
            open={agreementModal}
            onClose={() => {}}
            onAccept={() => {
              dispatch(
                editUser({
                  formData: { isPendingCheckAgreement: false },
                  userId: currentAdmin?.userId
                })
              );
              dispatch(getCurrentUser());
              dispatch(
                setPendingAgreementChecked({ isPendingCheckAgreement: false })
              );
              const data = JSON.parse(localStorage.getItem("admin"));
              localStorage.setItem(
                "admin",
                JSON.stringify({
                  ...data,
                  referenceData: {
                    ...data.referenceData,
                    isPendingCheckAgreement: false
                  }
                })
              );
              setAgreementModal(false);
            }}
          />
        )}
      <Box component="main" sx={{ flexGrow: 1 }}>
        <DrawerHeader
          style={{
            height: "95px"
          }}
        />
        <Box p={4}>
          {!currentAdmin?.adminUserId ? adminRenderContent() : renderContent()}
        </Box>
      </Box>
    </Box>
  );
};

export default AdminDashboard;
